/**
 * Banquet Wastage on the wastage screen — the pure rules, in one place.
 *
 * StockOutForm is a 5,000-line component, so everything that can be decided
 * without React lives here and is unit-tested on its own:
 *
 *   · which reason IS the banquet reason — by `system_code` / `is_banquet`, the
 *     same test the server makes (services/wastageReasonResolver.js). Never by
 *     label: the owner may rename it, and a hand-made "Banquet Wastage" reason
 *     is an ordinary reason.
 *   · how an event reads in the "Banquet event" dropdown;
 *   · which days the date picker may offer once an event is chosen;
 *   · the §12 messages for every refusal, and the success line + warnings.
 *
 * All dates are 'YYYY-MM-DD' IST calendar days, compared as strings (ISO days
 * sort correctly), and shifted with UTC arithmetic so the machine's own time
 * zone can never move a day.
 */

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const LONG_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Banquet wastage may be recorded from the event day up to this many days after. */
export const BANQUET_WASTAGE_WINDOW_DAYS = 3;

/** IST is UTC+05:30 all year (no DST), so a fixed offset is exact. */
const IST_OFFSET_MS = 330 * 60 * 1000;

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** 'YYYY-MM-DD' -> { y, m (1-12), d } or null. Accepts a full ISO timestamp's day part. */
function parseDay(value) {
  if (typeof value !== 'string') return null;
  const match = ISO_DAY.exec(value.split('T')[0]);
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  // Reject 31 Sep and friends rather than letting Date roll them over.
  const probe = new Date(Date.UTC(y, m - 1, d));
  if (probe.getUTCMonth() !== m - 1 || probe.getUTCDate() !== d) return null;
  return { y, m, d };
}

const pad2 = (n) => String(n).padStart(2, '0');

/** Shift a 'YYYY-MM-DD' day by `days`. null for an unreadable day. */
function shiftDay(value, days) {
  const p = parseDay(value);
  if (!p) return null;
  const t = new Date(Date.UTC(p.y, p.m - 1, p.d + days));
  return `${t.getUTCFullYear()}-${pad2(t.getUTCMonth() + 1)}-${pad2(t.getUTCDate())}`;
}

/** '2026-09-27' -> '27-09' */
function ddmm(value) {
  const p = parseDay(value);
  return p ? `${pad2(p.d)}-${pad2(p.m)}` : '';
}

/** '2026-09-27' -> '27-09-2026' */
function ddmmyyyy(value) {
  const p = parseDay(value);
  return p ? `${pad2(p.d)}-${pad2(p.m)}-${p.y}` : '';
}

/**
 * Is this the business's banquet reason?
 * The server's own test: `is_banquet === true || system_code === 'BANQUET'`.
 */
export function isBanquetReason(reason) {
  if (!reason || typeof reason !== 'object') return false;
  return reason.system_code === 'BANQUET' || reason.is_banquet === true;
}

/** '2026-09-27' -> '27 Sep'. '' when unreadable. */
export function formatShortDay(value) {
  const p = parseDay(value);
  return p ? `${p.d} ${SHORT_MONTHS[p.m - 1]}` : '';
}

/** A timestamp as the IST wall-clock time, '9:42 pm'. '' when unreadable. */
export function formatRecordedTime(value) {
  if (value === null || value === undefined || value === '') return '';
  const ms = new Date(value).getTime();
  if (!Number.isFinite(ms)) return '';
  const ist = new Date(ms + IST_OFFSET_MS);
  const hours = ist.getUTCHours();
  const minutes = ist.getUTCMinutes();
  const h12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${h12}:${pad2(minutes)} ${hours < 12 ? 'am' : 'pm'}`;
}

/**
 * One dropdown option: `Sharma Reception · 27 Sep · 100 plates · recorded 9:42 pm`.
 * The recorded time is what tells two functions on one day apart.
 */
export function formatEventOption(event) {
  if (!event) return '';
  const parts = [String(event.functionName || 'Banquet')];
  const day = formatShortDay(event.eventDate);
  if (day) parts.push(day);
  const plates = Number(event.platesOrdered);
  if (event.platesOrdered !== null && event.platesOrdered !== undefined && Number.isFinite(plates)) {
    parts.push(`${plates} ${plates === 1 ? 'plate' : 'plates'}`);
  }
  const time = formatRecordedTime(event.createdAt);
  if (time) parts.push(`recorded ${time}`);
  // A function recorded before its menu was settled served nothing, so nothing
  // can be wasted against it — said in the option, before it is picked.
  if (Array.isArray(event.dishes) && event.dishes.length === 0) parts.push('no dishes');
  return parts.join(' · ');
}

// ── Only what the function served (2026-10-25) ───────────────────────────────
//
// Banquet wastage names one of the function's DISHES, or a raw item or
// preparation INSIDE those dishes' recipes. The server sends that scope with
// every listed function (GET /banquet/wastage-events) and refuses anything
// outside it (BANQUET_ITEM_NOT_IN_FUNCTION); these helpers make the screen
// offer exactly the same set. The keys are the wastage grid's own: a dish by
// its menu item id, a raw item as `raw_<id>`, a preparation as `prep_<id>`.

/** Shown instead of the item grid until a function is picked. */
export const PICK_FUNCTION_FIRST =
  'Pick the function first. Banquet wastage can only be one of its dishes, or an ingredient or preparation inside them.';

/** The grid keys this function allows. An empty set for no function. */
export function functionItemKeys(event) {
  const keys = new Set();
  if (!event) return keys;
  for (const d of event.dishes || []) keys.add(String(d.menuItemId));
  for (const id of event.inventoryItemIds || []) keys.add(`raw_${id}`);
  for (const id of event.preparationIds || []) keys.add(`prep_${id}`);
  return keys;
}

/** The keys already entered that this function did not serve. */
export function keysOutsideFunction(wastedItems, event) {
  const allowed = functionItemKeys(event);
  return Object.keys(wastedItems || {}).filter((k) => !allowed.has(String(k)));
}

/** "Served: Biryani (100) · Dal (80)" — what the chosen function served. */
export function functionDishesNote(event) {
  const dishes = (event && event.dishes) || [];
  if (dishes.length === 0) return '';
  const fmt = (q) => {
    const n = Number(q);
    return Number.isInteger(n) ? String(n) : String(Math.round(n * 1000) / 1000);
  };
  return `Served: ${dishes.map((d) => `${d.itemName} (${fmt(d.quantity)})`).join(' · ')}`;
}

/** Shown in place of the grid when the chosen function has no dishes. */
export function emptyFunctionMessage(event) {
  const name = (event && event.functionName) || 'This function';
  return `No dishes were recorded for ${name}, so there is nothing to record against it. Pick another function.`;
}

/** The one-line notice after entries the function did not serve were removed. */
export function removedItemsNotice(names, functionName) {
  const list = (names || []).filter(Boolean);
  if (list.length === 0) return null;
  const what = list.length === 1 ? '1 item' : `${list.length} items`;
  return `Removed ${what} not served at ${functionName || 'this function'}: ${list.join(', ')}.`;
}

const RECORD_FIRST = 'Record the banquet first: Scan Bill → ⋮ → Banquet.';

/** The empty-dropdown guidance, naming the range the server searched. */
export function emptyEventsMessage(range) {
  const from = formatShortDay(range?.from);
  const to = formatShortDay(range?.to);
  if (from && to) return `No banquet recorded between ${from} and ${to}. ${RECORD_FIRST}`;
  return `No banquet recorded in the last ${BANQUET_WASTAGE_WINDOW_DAYS} days. ${RECORD_FIRST}`;
}

/** The listed event with this id (a <select> hands back strings), or null. */
export function findBanquetEvent(events, eventId) {
  if (!Array.isArray(events) || eventId === null || eventId === undefined || eventId === '') return null;
  const wanted = Number(eventId);
  if (!Number.isFinite(wanted)) return null;
  return events.find((e) => Number(e?.eventId) === wanted) || null;
}

const NO_WINDOW = Object.freeze({ minDate: null, maxDate: null });

/**
 * The days a wastage for `event` may be dated: the event day … min(windowTo,
 * today), and never on or before the reconciled day.
 *
 * @returns {{minDate: string|null, maxDate: string|null}}
 */
export function eventWindow(event, today, reconciledThrough) {
  if (!event || !parseDay(event.eventDate)) return { ...NO_WINDOW };

  const eventDay = event.eventDate.split('T')[0];
  let minDate = eventDay;
  if (parseDay(reconciledThrough) && reconciledThrough >= minDate) {
    minDate = shiftDay(reconciledThrough, 1);
  }

  let maxDate = parseDay(event.windowTo)
    ? event.windowTo.split('T')[0]
    : shiftDay(eventDay, BANQUET_WASTAGE_WINDOW_DAYS);
  if (parseDay(today) && today < maxDate) maxDate = today;

  return { minDate, maxDate };
}

/**
 * The amber note when the wastage lands in a later month than its banquet:
 * the expense follows the wastage date, the banquet's profit follows the event.
 */
export function monthBoundaryNote(wastageDate, eventDate) {
  const w = parseDay(wastageDate);
  const e = parseDay(eventDate);
  if (!w || !e) return null;
  if (w.y * 12 + w.m <= e.y * 12 + e.m) return null;
  return `This wastage will count in ${LONG_MONTHS[w.m - 1]}'s expenses and in ${LONG_MONTHS[e.m - 1]}'s banquet profit.`;
}

/**
 * What a batch item gains. An ordinary reason gains NOTHING — not even an
 * undefined key — so its payload stays byte-identical.
 */
export function banquetItemFields(reasonIsBanquet, event) {
  if (reasonIsBanquet !== true || !event) return {};
  const id = Number(event.eventId);
  if (!Number.isInteger(id) || id <= 0) return {};
  return { banquet_event_id: id };
}

/** The §12 copy for the codes whose message needs no data. */
export const BANQUET_MESSAGES = Object.freeze({
  BANQUET_LANE_OFF: 'Banquet is not enabled for this business.',
  BANQUET_EVENT_REQUIRED: 'Pick the banquet this wastage belongs to.',
  BANQUET_EVENT_NOT_FOUND: 'That banquet no longer exists — it may have been reverted. Pick again.',
  BANQUET_EVENT_WITHOUT_REASON: 'A banquet can only be attached to Banquet Wastage.',
  CLOSING_STOCK_IN_PROGRESS: 'A closing stock count is in progress. Finish or park it first.',
  BANQUET_NO_DRAFT: "Banquet wastage is submitted directly — it can't be saved as a draft.",
});

/** An object `details` only — an ARRAY `details` is the ingredient-shortage list. */
const objectDetails = (body) =>
  body && body.details && typeof body.details === 'object' && !Array.isArray(body.details)
    ? body.details
    : null;

/** The refusal's code: top-level `code`, or the batch's `details.reason_code` for PERIOD_RECONCILED. */
function refusalCode(body) {
  if (!body || typeof body !== 'object') return null;
  if (typeof body.code === 'string' && body.code) return body.code;
  const details = objectDetails(body);
  if (details && details.reason_code === 'PERIOD_RECONCILED') return 'PERIOD_RECONCILED';
  return null;
}

/** Is this a BANQUET_* refusal (one only the banquet flow can produce)? */
export function isBanquetRefusalCode(body) {
  const code = refusalCode(body);
  return typeof code === 'string' && code.startsWith('BANQUET_');
}

/**
 * The message to show for a refusal body (httpClient's `error.data`, or a
 * banquetApi `{ ok:false, code, error }` failure). null when the code is not
 * one of ours — the caller then keeps its existing handling.
 *
 * @param {object} body
 * @param {{event?: object|null}} [ctx] the chosen event, for names the body lacks
 */
export function banquetRefusalMessage(body, ctx = {}) {
  const code = refusalCode(body);
  if (!code) return null;
  const details = objectDetails(body) || {};

  if (Object.prototype.hasOwnProperty.call(BANQUET_MESSAGES, code)) return BANQUET_MESSAGES[code];

  if (code === 'BANQUET_WASTAGE_DATE') {
    const from = ddmm(details.eventDate);
    const to = ddmm(details.allowedTo);
    const range = from && to ? ` (${from} – ${to})` : '';
    return `Banquet wastage can be recorded from the event date up to ${BANQUET_WASTAGE_WINDOW_DAYS} days after${range}.`;
  }

  if (code === 'PERIOD_RECONCILED') {
    const through = ddmmyyyy(details.reconciled_through || details.reconciledThrough || body.reconciledThrough);
    return through
      ? `Closing stock is confirmed up to ${through}. Entries on or before that date can't be added or reverted.`
      : "Closing stock is confirmed for this period. Entries on or before the confirmed date can't be added or reverted.";
  }

  if (code === 'BANQUET_ITEM_NOT_IN_FUNCTION') {
    // The server names the item and the function; prefer its sentence.
    if (typeof body.error === 'string' && body.error.trim()) return body.error.trim();
    const name = details.functionName || ctx?.event?.functionName;
    return `That item was not served at ${name || 'this function'}. Banquet wastage can only be one of its dishes, or an ingredient or preparation inside them.`;
  }

  if (code === 'BANQUET_PERIOD_RECONCILED') {
    if (typeof body.error === 'string' && body.error.trim()) return body.error;
    const name = ctx?.event?.functionName;
    const day = ddmm(details.eventDate || ctx?.event?.eventDate);
    const label = [name, day].filter(Boolean).join(', ');
    return `This banquet${label ? ` (${label})` : ''} is inside a period closed by a confirmed closing stock count.`;
  }

  return null;
}

/** Sum of the 201's `data[].estimated_cost` (strings from toFixed). */
export function sumEstimatedCost(rows) {
  if (!Array.isArray(rows)) return 0;
  const total = rows.reduce((sum, row) => {
    const n = Number(row?.estimated_cost);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
  return Math.round(total * 100) / 100;
}

const formatRupees = (amount) =>
  `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(Number(amount) || 0)}`;

/** 'Banquet wastage recorded — Sharma Reception · 2 items · ₹2,250' */
export function banquetSuccessMessage({ functionName, itemCount, totalCost }) {
  const count = Number(itemCount) || 0;
  return `Banquet wastage recorded — ${functionName || 'Banquet'} · ${count} ${count === 1 ? 'item' : 'items'} · ${formatRupees(totalCost)}`;
}

/** The server's ready-to-show banquet warnings (codes BANQUET_*). */
export function banquetWarningMessages(warnings) {
  if (!Array.isArray(warnings)) return [];
  return warnings
    .filter((w) => w && typeof w.code === 'string' && w.code.startsWith('BANQUET_'))
    .map((w) => (typeof w.message === 'string' ? w.message.trim() : ''))
    .filter(Boolean);
}
